<template>
  <div class="card">
    <div class="card-header"><i class="icon-refresh"></i> Filtrer</div>
    <div class="card-block">
      <div class="row">
        <div class="form-group col-sm-6">
          <label
            >Date de début
            <i
              class="fa fa-info-circle ml-05"
              v-tooltip="{
                content:
                  'Ceci est le menu regroupant les factures et avoirs. Les filtres de dates sont donc basés sur la date de création de facture/avoir.',
              }"
            ></i>
          </label>
          <datepicker
            v-model="form.begin"
            format="dd.MM.yyyy"
            language="fr"
            input-class="form-control"
          ></datepicker>
        </div>
        <div class="form-group col-sm-6">
          <label>Date de fin</label>
          <datepicker
            v-model="form.end"
            format="dd.MM.yyyy"
            language="fr"
            input-class="form-control"
          ></datepicker>
        </div>
      </div>
      <div class="row">
        <div class="form-group col-sm-4">
          <label>Filtrer par société</label>
          <v-select
            :options="merchantsOptions"
            v-model="form.merchant"
          ></v-select>
        </div>

        <div class="form-group col-sm-4">
          <label
            >Filtrer par concept
            <i
              v-if="stats"
              class="fa fa-info-circle ml-05"
              v-tooltip="{
                content:
                  'Par défaut, aucun filtre n\'est appliqué et tous les concepts sont inclus dans les résultats.',
              }"
            ></i>
          </label>
          <multiselect
            v-model="form.concept"
            :options="conceptsOptions"
            :multiple="stats"
            class="custom-multiselect"
          >
          </multiselect>
        </div>

        <div class="form-group col-sm-4" v-if="invoiceSeries && !stats">
          <label>Filtrer par code</label>
          <v-select
            :options="
              invoiceSeries
                .filter((is) => is.code !== 'VISIO')
                .map((is) => is.code)
            "
            v-model="form.invoiceSerieCode"
          ></v-select>
        </div>

        <div
          class="form-group"
          v-if="!stats"
          :class="!unpaid && !stats ? 'col-sm-3' : 'col-sm-4'"
        >
          <label>Filtrer par client</label>
          <v-select
            v-model="form.client"
            :options="clients"
            @search="searchClients"
          ></v-select>
        </div>

        <div class="form-group col-sm-3" v-if="!unpaid && !stats">
          <label>Filtrer par statut</label>
          <multiselect
            v-model="form.status"
            :options="orderStatus"
            :multiple="true"
            label="label"
            track-by="label"
            class="custom-multiselect"
          >
          </multiselect>
        </div>
        <div
          class="form-group"
          v-if="!stats"
          :class="!unpaid ? 'col-sm-3' : 'col-sm-4'"
        >
          <label>Filtrer par numéro de facture</label>
          <input
            class="form-control"
            placeholder="202103COMP"
            v-model="form.match"
          />
        </div>
        <div class="form-group" :class="!unpaid ? 'col-sm-3' : 'col-sm-4'">
          <label>Commercial</label>
          <multiselect
            v-model="form.commercials"
            :options="commercials"
            :multiple="true"
            label="label"
            track-by="label"
            class="custom-multiselect"
          >
          </multiselect>
        </div>

        <div class="form-group" :class="!unpaid ? 'col-sm-3' : 'col-sm-4'">
          <label>Filtrer par département</label>
          <multiselect
            v-model="form.zipcodes"
            :options="zipcodes"
            :multiple="true"
            label="label"
            track-by="label"
            class="custom-multiselect"
          >
          </multiselect>
        </div>

        <div class="form-group" :class="!unpaid ? 'col-sm-3' : 'col-sm-4'">
          <label>Filtrer par type de commande</label>
          <v-select
            v-model="form.type"
            :options="orderTypes"
            placeholder="Tout"
          ></v-select>
        </div>
      </div>
      <div class="col-md-12" v-if="stats">
        <span style="font-italic">
          ”Ceci est le menu regroupant données liées au C.A en terme
          d’Acquisition et Fidélisation. Les filtres de dates sont donc basés
          sur la date de création de facture/avoir. Ceci comprend donc
          l’ensemble des factures payées, partiellement payées et avoirs. Ce qui
          nous permet d’obtenir les données chiffrées au global et par
          commercial sur la période”
        </span>
      </div>
    </div>
    <div class="card-header">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        @click="loadInvoices()"
      >
        <i class="icon-eye"></i>&nbsp; Visualiser
      </button>
    </div>
  </div>
</template>

<script>
import debounce from "lodash.debounce";
import { mapState } from "vuex";
import vSelect from "vue-select";
import Datepicker from "vuejs-datepicker";
import Multiselect from "vue-multiselect";

export default {
  components: {
    vSelect,
    Datepicker,
    Multiselect,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    commercials: {
      type: Array,
      required: true,
    },
    zipcodes: {
      type: Array,
      default: () => [],
    },
    invoiceSeries: {
      type: Array,
      required: true,
    },
    unpaid: {
      type: Boolean,
      default: false,
    },
    stats: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      orderStatus: [
        { label: "Payée", value: "payed" },
        { label: "Partiel", value: "partial" },
        { label: "Avoir", value: "refunded" },
        { label: "Impayé (traité)", value: "unpaid" },
      ],
      clients: [],
      orderTypes: [
        { label: "Conversion", value: "conversion" },
        { label: "Acquisition", value: "acquisition" },
        { label: "Fidélisation", value: "fidelisation" },
        { label: "Découverte", value: "discovery" },
      ],
      form: {},
    };
  },

  computed: {
    ...mapState({
      merchants: (state) => state.merchants,
      concepts: (state) => state.concepts,
    }),

    merchantsOptions() {
      return (
        this.merchants?.map((m) => ({
          label: m.name,
          value: m.id,
        })) || []
      );
    },

    conceptsOptions() {
      return this.concepts?.map((c) => c.name) || [];
    },
  },

  mounted() {
    this.form = this.filters;
  },

  methods: {
    async searchClients(search, loading) {
      if (search !== "") {
        await this.fetchClients(search, loading, this);
      }
    },

    fetchClients: debounce(async (search, loading, vm) => {
      if (!search || !search.length > 3) {
        return;
      }

      loading(true);

      const { data } = await vm.$api.get(`/clients/filter?q=${search}`);

      vm.clients = data.map((c) => ({
        label: c.name + " (" + c.email + ")",
        value: c.email,
      }));

      loading(false);

      return vm.clients;
    }, 300),

    loadInvoices() {
      this.$emit("load", this.form);
    },
  },
};
</script>
